import { useCallback, useState } from 'react';
import fleetApi from '../../services/api.service';

/**
 * Hook custom pour optimiser la séquence de livraison d'un véhicule spécifique avec VROOM
 * 
 * Cette optimisation permet de réorganiser automatiquement l'ordre des livraisons
 * pour un camion donné afin de minimiser la distance et le temps de trajet.
 * 
 * Retourne:
 * - optimizeVehicle: fonction pour lancer l'optimisation d'un véhicule
 * - isOptimizing: état de chargement
 * - error: erreur éventuelle
 * - optimizationResult: résultats de l'optimisation (route optimisée)
 * - resetOptimization: fonction pour réinitialiser les résultats
 */
const useOptimizeVehicle = () => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [error, setError] = useState(null);
  const [optimizationResult, setOptimizationResult] = useState(null);

  /**
   * Optimise et applique automatiquement la séquence de livraison pour un véhicule
   * 
   * @param {Object} params - Paramètres de l'optimisation
   * @param {number} params.vehicleId - ID du véhicule à optimiser
   * @param {string} params.tourDate - Date de la tournée (format YYYY-MM-DD)
   * @returns {Promise<Object>} Résultat de l'optimisation
   */
  const optimizeVehicle = useCallback(async ({ vehicleId, tourDate } = {}) => {
    setIsOptimizing(true);
    setError(null);
    setOptimizationResult(null);

    try {
      if (!vehicleId) {
        throw new Error('L\'ID du véhicule est requis pour l\'optimisation.');
      }

      if (!tourDate) {
        throw new Error('La date de tournée est requise pour l\'optimisation.');
      }

      const payload = {
        date: tourDate,
        vehicleKey: `vehicle_${vehicleId}`,
        autoApply: true, // Applique automatiquement la séquence optimisée
        optimizationOptions: {
          returnGeometry: true, // Pour afficher l'itinéraire sur la carte
        },
      };

      const response = await fleetApi.post(
        `/tours-planning/vehicles/${vehicleId}/optimize-and-apply`,
        payload
      );

      if (response.status === 200 && response.data) {
        const optimizationData = response.data?.data ?? response.data;
        
        setOptimizationResult(optimizationData);
        return {
          success: true,
          data: optimizationData,
          message: response.data?.message || 'Optimisation du véhicule réussie',
        };
      }

      throw new Error('Réponse invalide du serveur');
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err.message ||
        "Impossible d'optimiser la séquence du véhicule";
      setError(message);
      return { success: false, message };
    } finally {
      setIsOptimizing(false);
    }
  }, []);

  const resetOptimization = useCallback(() => {
    setOptimizationResult(null);
    setError(null);
  }, []);

  return {
    optimizeVehicle,
    isOptimizing,
    error,
    optimizationResult,
    resetOptimization,
  };
};

export default useOptimizeVehicle;

